import { FormEvent, useEffect, useMemo, useState } from 'react';
import { Search, Calendar, ArrowUpRight, Filter, Tag, Plus, Rss, X, Newspaper, ChevronDown } from 'lucide-react';
import { Input } from '@/components/ui/Input';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Card, CardContent } from '@/components/ui/Card';
import { KeywordChips } from '@/components/ui/KeywordChips';
import { UfMultiSelect } from '@/components/ui/UfMultiSelect';
import { useCreateNoticiaFonte, useNoticiasFeed, useNoticiasFontes } from '@/hooks/useNoticias';
import { useAtualizarMatchingConfig, useMatchingConfig } from '@/hooks/useEmpresa';
import { useViewModeStore } from '@/stores/view-mode';

const formatDate = (dateString: string) => {
  const date = new Date(dateString);
  return date.toLocaleDateString('pt-BR', { 
    day: 'numeric', 
    month: 'short', 
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

const detectCategory = (title: string, summary: string | null) => {
  const text = `${title} ${summary || ''}`.toLowerCase();

  if (text.includes('licit') || text.includes('edital') || text.includes('pregao')) {
    return 'licitacao';
  }
  if (text.includes('infraestrutura') || text.includes('obra') || text.includes('rodovia')) {
    return 'infraestrutura';
  }
  if (text.includes('lei') || text.includes('decreto') || text.includes('regul')) {
    return 'regulamentacao';
  }

  return 'mercado';
};

const getCategoryLabel = (category: 'licitacao' | 'infraestrutura' | 'regulamentacao' | 'mercado') => {
  const labels: Record<'licitacao' | 'infraestrutura' | 'regulamentacao' | 'mercado', string> = {
    licitacao: 'Licitação',
    infraestrutura: 'Infraestrutura',
    regulamentacao: 'Regulamentação',
    mercado: 'Mercado',
  };
  return labels[category];
};

const getCategoryVariant = (category: 'licitacao' | 'infraestrutura' | 'regulamentacao' | 'mercado'): 'success' | 'info' | 'warning' | 'default' => {
  const variants: Record<'licitacao' | 'infraestrutura' | 'regulamentacao' | 'mercado', 'success' | 'info' | 'warning' | 'default'> = {
    licitacao: 'success',
    infraestrutura: 'info',
    regulamentacao: 'warning',
    mercado: 'default',
  };
  return variants[category];
};

const buildSummary = (title: string, summary: string | null, content: string | null) => {
  const sourceText = (summary || content || '').trim();
  if (!sourceText) {
    return `Resumo: ${title}. Não há conteúdo detalhado suficiente para sumarização automática.`;
  }

  const plain = sourceText.replace(/\s+/g, ' ').trim();
  const sentences = plain
    .split(/(?<=[.!?])\s+/)
    .filter(Boolean)
    .slice(0, 3)
    .join(' ');

  const compact = (sentences || plain).substring(0, 420).trim();
  return `Resumo: ${compact}${compact.endsWith('.') ? '' : '.'}`;
};

interface FiltroFeedForm {
  palavrasChave: string[];
  palavrasChaveExclusao: string[];
  ufs: string[];
  scoreMinimo: number;
}

/**
 * Edita a mesma empresas_matching_config da aba Funil (fonte única), mas com
 * apresentação própria da página de Notícias: sem toggle de ativação, sem
 * botão de varredura e sem o painel explicativo do funil.
 */
function FiltroNoticiasMeuFeed({ empresaId }: { empresaId: number }) {
  const [aberto, setAberto] = useState(false);
  const [form, setForm] = useState<FiltroFeedForm | null>(null);
  const [successMessage, setSuccessMessage] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const empresaIdStr = String(empresaId);
  const { data, isLoading } = useMatchingConfig(empresaIdStr);
  const mutation = useAtualizarMatchingConfig(empresaIdStr);

  useEffect(() => {
    if (!data) return;
    setForm({
      palavrasChave: data.palavrasChave,
      palavrasChaveExclusao: data.palavrasChaveExclusao,
      ufs: data.ufs ?? [],
      scoreMinimo: data.scoreMinimo,
    });
  }, [data]);

  const handleSave = () => {
    if (!form) return;
    setSuccessMessage(false);
    setErrorMessage(null);
    mutation.mutate(
      {
        palavras_chave: form.palavrasChave,
        palavras_chave_exclusao: form.palavrasChaveExclusao,
        ufs: form.ufs,
        score_minimo: form.scoreMinimo,
      },
      {
        onSuccess: () => setSuccessMessage(true),
        onError: () => setErrorMessage('Não foi possível salvar o filtro de notícias. Tente novamente.'),
      }
    );
  };

  return (
    <Card>
      <CardContent className="pt-6">
        <button
          type="button"
          onClick={() => setAberto((value) => !value)}
          aria-expanded={aberto}
          className="flex w-full items-center justify-between gap-3 text-left"
        >
          <span className="flex items-center gap-2">
            <Newspaper className="h-5 w-5 text-ds-brand" />
            <span className="text-base font-semibold text-ds-text-primary">Filtro de notícias do Meu Feed</span>
          </span>
          <ChevronDown className={`h-4 w-4 text-ds-text-secondary transition-transform ${aberto ? 'rotate-180' : ''}`} />
        </button>

        {aberto && (
          <div className="mt-4 space-y-5">
            <p className="text-sm text-ds-text-secondary">
              Estes são os mesmos termos do funil da empresa (Minha Empresa → Funil). Eles definem quais notícias
              aparecem no card "Notícias do seu nicho" do Meu Feed.
            </p>

            {isLoading || !form ? (
              <div className="flex items-center justify-center py-6" role="status">
                <div className="h-6 w-6 rounded-full border-2 border-ds-border border-t-ds-brand animate-spin" />
              </div>
            ) : (
              <>
                {successMessage && (
                  <div className="rounded-lg border border-ds-success/30 bg-ds-success/10 p-3 text-sm text-ds-success">
                    Filtro de notícias salvo. O card do Meu Feed passa a usar os novos termos.
                  </div>
                )}

                {errorMessage && (
                  <div className="rounded-lg border border-ds-danger/30 bg-ds-danger/10 p-3 text-sm text-ds-danger">
                    {errorMessage}
                  </div>
                )}

                <KeywordChips
                  label="Palavras-chave de inclusão"
                  value={form.palavrasChave}
                  onChange={(value) => setForm({ ...form, palavrasChave: value ?? [] })}
                  placeholder="Digite uma palavra-chave e pressione Enter"
                />

                <KeywordChips
                  label="Palavras-chave de exclusão"
                  value={form.palavrasChaveExclusao}
                  onChange={(value) => setForm({ ...form, palavrasChaveExclusao: value ?? [] })}
                  placeholder="Digite uma palavra a excluir e pressione Enter"
                />

                <UfMultiSelect
                  value={form.ufs}
                  onChange={(value) => setForm({ ...form, ufs: value })}
                />

                <Input
                  type="number"
                  min={0}
                  max={100}
                  step={1}
                  label="Score mínimo para considerar a notícia coerente (0-100)"
                  value={form.scoreMinimo}
                  onChange={(e) => {
                    const parsed = parseInt(e.target.value, 10);
                    setForm({ ...form, scoreMinimo: Number.isNaN(parsed) ? 0 : Math.min(100, Math.max(0, parsed)) });
                  }}
                />

                <div className="flex justify-end">
                  <Button onClick={handleSave} isLoading={mutation.isPending}>
                    Salvar filtro de notícias
                  </Button>
                </div>
              </>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function Noticias() {
  const { mode, previewEmpresaId } = useViewModeStore();
  const [searchTerm, setSearchTerm] = useState('');
  const [sourceFilter, setSourceFilter] = useState<string>('all');
  const [showAddSource, setShowAddSource] = useState(false);
  const [selectedNoticia, setSelectedNoticia] = useState<{
    id: number;
    titulo: string;
    resumo: string | null;
    conteudo: string | null;
    fonte: string | null;
    url: string | null;
    fonte_url: string | null;
    data_publicacao: string | null;
    created_at: string;
  } | null>(null);
  const [formData, setFormData] = useState({
    nome: '',
    url: '',
    tipo: 'html' as 'rss' | 'html' | 'api',
    rss_url: '',
    api_url: '',
    palavras: ''
  });

  const { data: fontes = [] } = useNoticiasFontes();
  const { data: feedData, isLoading, isError } = useNoticiasFeed({
    search: searchTerm || undefined,
    source: sourceFilter === 'all' ? undefined : sourceFilter,
    page: 1,
    limit: 50
  });
  const createFonteMutation = useCreateNoticiaFonte();

  const noticias = feedData?.noticias || [];
  const sourceOptions = useMemo(() => {
    const fromFontes = fontes.map((fonte) => fonte.nome);
    const fromNoticias = noticias.map((noticia) => noticia.fonte).filter(Boolean) as string[];
    return Array.from(new Set([...fromFontes, ...fromNoticias])).sort((a, b) => a.localeCompare(b));
  }, [fontes, noticias]);

  const handleAddSource = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const keywords = formData.palavras
      .split(',')
      .map((item) => item.trim())
      .filter(Boolean);

    await createFonteMutation.mutateAsync({
      nome: formData.nome,
      url: formData.url,
      tipo: formData.tipo,
      rss_url: formData.tipo === 'rss' ? formData.rss_url || undefined : undefined,
      api_url: formData.tipo === 'api' ? formData.api_url || undefined : undefined,
      palavras_chave: keywords.length > 0 ? keywords : undefined
    });

    setFormData({ nome: '', url: '', tipo: 'html', rss_url: '', api_url: '', palavras: '' });
    setShowAddSource(false);
  };

  const mutationErrorMessage = (createFonteMutation.error as { message?: string } | null)?.message || null;

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-ds-text-primary">Notícias</h1>
          <p className="text-ds-text-secondary mt-1">
            Feed real de coletas diárias sobre licitações e infraestrutura
          </p>
        </div>

        <Button
          type="button"
          className="min-h-[44px]"
          onClick={() => setShowAddSource((value) => !value)}
        >
          <Plus className="h-4 w-4 mr-2" />
          ADICIONAR FONTE
        </Button>
      </div>

      {showAddSource && (
        <Card>
          <CardContent className="pt-6">
            <form className="space-y-4" onSubmit={handleAddSource}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Input
                  label="Nome da fonte"
                  placeholder="Ex.: Portal de Compras XYZ"
                  value={formData.nome}
                  onChange={(e) => setFormData((prev) => ({ ...prev, nome: e.target.value }))}
                  required
                />

                <Input
                  label="URL principal"
                  placeholder="https://exemplo.com/noticias"
                  value={formData.url}
                  onChange={(e) => setFormData((prev) => ({ ...prev, url: e.target.value }))}
                  required
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-ds-text-secondary mb-2">
                    Tipo da fonte
                  </label>
                  <select
                    value={formData.tipo}
                    onChange={(e) => setFormData((prev) => ({ ...prev, tipo: e.target.value as 'rss' | 'html' | 'api' }))}
                    className="w-full bg-ds-surface2 border border-ds-border rounded-lg px-3 py-2.5 text-sm text-ds-text-primary focus:outline-none focus:ring-2 focus:ring-ds-success/50"
                  >
                    <option value="html">HTML</option>
                    <option value="rss">RSS</option>
                    <option value="api">API</option>
                  </select>
                </div>

                <Input
                  label="Palavras-chave (opcional)"
                  placeholder="licitação, pregão, edital"
                  value={formData.palavras}
                  onChange={(e) => setFormData((prev) => ({ ...prev, palavras: e.target.value }))}
                />
              </div>

              {formData.tipo === 'rss' && (
                <Input
                  label="URL do RSS"
                  placeholder="https://exemplo.com/feed.xml"
                  value={formData.rss_url}
                  onChange={(e) => setFormData((prev) => ({ ...prev, rss_url: e.target.value }))}
                  required
                />
              )}

              {formData.tipo === 'api' && (
                <Input
                  label="URL da API"
                  placeholder="https://api.exemplo.com/noticias"
                  value={formData.api_url}
                  onChange={(e) => setFormData((prev) => ({ ...prev, api_url: e.target.value }))}
                  required
                />
              )}

              {mutationErrorMessage && (
                <p className="text-sm text-ds-danger">{mutationErrorMessage}</p>
              )}

              <div className="flex justify-end gap-3">
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => setShowAddSource(false)}
                >
                  Cancelar
                </Button>
                <Button type="submit" isLoading={createFonteMutation.isPending}>
                  Salvar fonte
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-ds-text-secondary" />
              <Input
                placeholder="Buscar notícias..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            <div className="flex items-center gap-2">
              <Filter className="h-4 w-4 text-ds-text-secondary" />
              <select
                value={sourceFilter}
                onChange={(e) => setSourceFilter(e.target.value)}
                className="bg-ds-surface2 border border-ds-border rounded-lg px-3 py-2 text-sm text-ds-text-primary focus:outline-none focus:ring-2 focus:ring-ds-success/50"
              >
                <option value="all">Todas as fontes</option>
                {sourceOptions.map((sourceName) => (
                  <option key={sourceName} value={sourceName}>
                    {sourceName}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </CardContent>
      </Card>

      {mode === 'empresa' && previewEmpresaId != null && (
        <FiltroNoticiasMeuFeed empresaId={previewEmpresaId} />
      )}

      {/* Notícias list */}
      {isError && (
        <Card>
          <CardContent className="py-8 text-center">
            <p className="text-ds-danger">Erro ao carregar notícias. Tente novamente em instantes.</p>
          </CardContent>
        </Card>
      )}

      {isLoading && (
        <Card>
          <CardContent className="py-10 text-center text-ds-text-secondary">
            Carregando notícias coletadas...
          </CardContent>
        </Card>
      )}

      {!isLoading && !isError && (
        <div className="space-y-4">
          {noticias.map((noticia) => {
            const category = detectCategory(noticia.titulo, noticia.resumo);
            const resolvedUrl = noticia.url || noticia.fonte_url;
            const tags = [
              noticia.fonte || 'Fonte não informada',
              getCategoryLabel(category),
              'coleta diária'
            ];

            return (
              <Card
                key={noticia.id}
                variant="hover"
                className="cursor-pointer"
                role="button"
                tabIndex={0}
                onClick={() => setSelectedNoticia(noticia)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    setSelectedNoticia(noticia);
                  }
                }}
                aria-label={`Abrir resumo da notícia: ${noticia.titulo}`}
              >
                <CardContent className="pt-6">
                  <div className="flex flex-col gap-4">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <Badge variant={getCategoryVariant(category)}>
                            {getCategoryLabel(category)}
                          </Badge>
                          <span className="text-xs text-ds-text-secondary flex items-center gap-1">
                            <Calendar className="h-3 w-3" />
                            {formatDate(noticia.data_publicacao || noticia.created_at)}
                          </span>
                        </div>

                        <h3 className="text-lg font-semibold text-ds-text-primary mb-2">
                          {noticia.titulo}
                        </h3>

                        <p className="text-ds-text-secondary mb-3">
                          {noticia.resumo || noticia.conteudo || 'Sem resumo disponível.'}
                        </p>

                        <div className="flex flex-wrap items-center gap-4">
                          <span className="text-sm text-ds-text-secondary flex items-center gap-1.5">
                            <Rss className="h-3.5 w-3.5" />
                            Fonte: <span className="text-ds-text-primary font-medium">{noticia.fonte || 'Não informada'}</span>
                          </span>

                          <div className="flex items-center gap-2">
                            <Tag className="h-3.5 w-3.5 text-ds-text-secondary" />
                            <div className="flex gap-1.5 flex-wrap">
                              {tags.map((tag) => (
                                <span
                                  key={`${noticia.id}-${tag}`}
                                  className="text-xs text-ds-text-secondary bg-ds-surface2 px-2 py-0.5 rounded"
                                >
                                  {tag}
                                </span>
                              ))}
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="flex flex-col gap-2 items-end">
                        {resolvedUrl && (
                          <a
                            href={resolvedUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="p-2 rounded-lg hover:bg-ds-surface2 text-ds-text-secondary hover:text-ds-success transition-colors"
                            aria-label="Abrir notícia na fonte"
                            onClick={(event) => event.stopPropagation()}
                          >
                            <ArrowUpRight className="h-5 w-5" />
                          </a>
                        )}

                        {resolvedUrl && (
                          <a
                            href={resolvedUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={(event) => event.stopPropagation()}
                          >
                            <Button type="button" size="sm" className="min-h-[44px]">
                              Abrir notícia
                            </Button>
                          </a>
                        )}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {!isLoading && !isError && noticias.length === 0 && (
        <Card>
          <CardContent className="py-12 text-center">
            <p className="text-ds-text-secondary">
              Nenhuma notícia encontrada com os filtros selecionados.
            </p>
          </CardContent>
        </Card>
      )}

      {selectedNoticia && (
        <div
          className="fixed inset-0 z-50 bg-black/50 p-4 md:p-8"
          onClick={() => setSelectedNoticia(null)}
          role="presentation"
        >
          <div
            className="mx-auto mt-4 w-full max-w-3xl rounded-xl border border-ds-border bg-ds-surface shadow-2xl"
            onClick={(event) => event.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-labelledby="noticia-resumo-title"
          >
            <div className="flex items-start justify-between gap-4 border-b border-ds-border p-5">
              <div>
                <h2 id="noticia-resumo-title" className="text-xl font-semibold text-ds-text-primary">
                  {selectedNoticia.titulo}
                </h2>
                <p className="mt-1 text-sm text-ds-text-secondary">
                  {selectedNoticia.fonte || 'Fonte não informada'} • {formatDate(selectedNoticia.data_publicacao || selectedNoticia.created_at)}
                </p>
              </div>

              <button
                type="button"
                onClick={() => setSelectedNoticia(null)}
                className="rounded-lg p-2 text-ds-text-secondary hover:bg-ds-surface2 hover:text-ds-text-primary"
                aria-label="Fechar resumo"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="space-y-4 p-5">
              <div className="rounded-lg border border-ds-border bg-ds-surface2 p-4">
                <p className="text-sm font-medium text-ds-text-primary mb-2">Sumarização</p>
                <p className="text-sm leading-relaxed text-ds-text-secondary">
                  {buildSummary(selectedNoticia.titulo, selectedNoticia.resumo, selectedNoticia.conteudo)}
                </p>
              </div>

              <div>
                <p className="text-sm font-medium text-ds-text-primary mb-2">Resumo original</p>
                <p className="text-sm leading-relaxed text-ds-text-secondary">
                  {selectedNoticia.resumo || selectedNoticia.conteudo || 'Sem conteúdo detalhado disponível para esta notícia.'}
                </p>
              </div>

              <div className="flex justify-end gap-3 pt-2">
                <Button type="button" variant="ghost" onClick={() => setSelectedNoticia(null)}>
                  Fechar
                </Button>
                {(selectedNoticia.url || selectedNoticia.fonte_url) && (
                  <a href={selectedNoticia.url || selectedNoticia.fonte_url || undefined} target="_blank" rel="noopener noreferrer">
                    <Button type="button" className="min-h-[44px]">
                      Abrir notícia
                    </Button>
                  </a>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export { Noticias };
